/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  BandWidthOption,
  ConnectionEvent,
  ConnectionState,
  ErrorCode,
  LocalAudioTrack,
  LocalScreenTrack,
  LocalVideoTrack,
  NAME,
  NOT_SUPPORTED_H264,
  RtcError,
  TrackEvent,
  TRANSPORT_DIRECTION,
  SSRC,
  ILocalTrack,
  SMALL_STAT_BIT,
  VIDEO_STAT_BIT,
  AUDIO_STAT_BIT,
  AUX_STAT_BIT,
  MediaType,
  VideoCodec,
  MULTI_VIDEO_DATA_TYPE,
  SignalResponse,
  LocalTrackEvent,
  TIMER_TYPE,
  defaultBigVideoProfile,
  SMALL_MODE,
  RemoteStreamType,
  rtcDetector,
  LocalScreenAudioTrack
} from 'trtc-js-sdk-core';

import {
  IS_GET_SETTINGS_SUPPORTED,
  IS_SCRIPT_TRANSFORM_SUPPORTED,
  isAddTransceiverSupported,
  isCanvasSmallStreamSupported,
  isGetTransceiversSupported
} from 'trtc-js-sdk-core/src/common/rtc-detection';

import { SignalCommand, SignalEvent } from '../../signal/constant';
import { IOS_VERSION, IS_ANDROID, IS_IOS, IS_WECHAT, SAFARI_VERSION, getChromeMajorVersion, getDeviceModel, getOSString, isAMDGPU, isVersionLargerThan } from 'trtc-js-sdk-core/src/utils/environment';
import { generateErrorMessage } from 'trtc-js-sdk-core/src/common/error-generator';
import { ErrorInfo, ErrorName } from 'trtc-js-sdk-core/src/common/error-info';
import INNER_EVENT from 'trtc-js-sdk-core/src/inner-emitter/inner-event';
import innerEmitter from 'trtc-js-sdk-core/src/inner-emitter/inner-emitter';
import ConnectionSPC from './connection-spc';
import SinglePeerConnection, { SPC_EVENT } from './single-peerconnection';
import { IUplinkConnection } from '../../type/type';
import kvStatManager, { KV_REPORT_KEY_NETWORK, KV_REPORT_KEY_SMALL_MODE, SMALL_MODE_ERROR_CODE } from 'trtc-js-sdk-core/src/manager/kv-stat-manager';
import Timer from 'trtc-js-sdk-core/src/utils/timer';
import { after } from 'trtc-js-sdk-core/src/utils/decorators/middleware';
import { skipIf } from 'trtc-js-sdk-core/src/utils/decorators/skip-if';
import { UplinkConnectionSPCOption } from '../type';
import { calculateScaleResolutionDownNumber } from 'trtc-js-sdk-core/src/utils/index';
import blackVideoDetector from 'trtc-js-sdk-core/src/utils/black-video-detector';

/**
 * 继承自 Connection 类，将上行相关功能逻辑从 Connection 中分离
 * 除了提供基础 Connection 功能之外，还提供上行重连功能
 * @class UplinkConnection
 * @extends {ConnectionSPC}
 */
class UplinkConnectionSPC extends ConnectionSPC implements IUplinkConnection {
  localMainAudioTrack?: LocalAudioTrack | null = null;
  localMainVideoTrack?: LocalVideoTrack | LocalScreenTrack | null = null;
  localAuxAudioTrack?: LocalAudioTrack | null = null;
  localAuxVideoTrack?: LocalVideoTrack | LocalScreenTrack | null = null;
  private _isPublishingAux = false;
  private _publishingLocalAudioTrack: LocalAudioTrack | null;
  private _publishingLocalVideoTrack: LocalVideoTrack | null;
  private _mediaSettings = {
    videoCodec: '',
    videoWidth: 0,
    videoHeight: 0,
    videoBps: 0, // bps
    videoFps: 0,
    videoDecCodec: '',

    audioCodec: 'opus',
    audioFs: 0, // 采样率
    audioChannel: 0, // 声道数
    audioBps: 0, // 单位bps

    smallVideoWidth: 0,
    smallVideoHeight: 0,
    smallVideoFps: 0,
    smallVideoBps: 0,

    auxVideoWidth: 0,
    auxVideoHeight: 0,
    auxVideoFps: 0,
    auxVideoBps: 0
  };

  private _flag = 0;
  private _checkPublishStateTimeoutId = -1;

  constructor(options: UplinkConnectionSPCOption) {
    super({ ...options, isUplink: true });

    this.initialize();
  }

  get videoCodec() {
    return this.singlePC?.videoCodec || VideoCodec.H264;
  }

  get ssrc(): SSRC {
    if (!this.singlePC) {
      return { audio: 0, video: 0, videoRtx: 0, small: 0, smallRtx: 0, auxiliary: 0, auxiliaryRtx: 0 };
    }
    const { audioSsrc, bigVideoSsrc, bigVideoRtxSsrc, smallVideoSsrc, smallVideoRtxSsrc, auxVideoSsrc, auxVideoRtxSsrc } = this.singlePC.uplinkSSRC;
    return {
      audio: audioSsrc || 0,
      video: bigVideoSsrc || 0,
      videoRtx: bigVideoRtxSsrc || 0,
      small: smallVideoSsrc || 0,
      smallRtx: smallVideoRtxSsrc || 0,
      auxiliary: auxVideoSsrc || 0,
      auxiliaryRtx: auxVideoRtxSsrc || 0
    };
  }

  get flag() {
    return this._flag;
  }

  set flag(value: number) {
    if (this._flag !== value) {
      this._flag = value;
      this.checkPublishState();
    }
  }

  checkPublishState(isSecondCheck = false) {
    try {
      if (!isSecondCheck && this._checkPublishStateTimeoutId > 0) return;

      const { serverPublishState } = this;
      const { publishState } = this._room;
      const notMatchKeyList = (Object.keys(publishState) as Array<keyof typeof publishState>).filter(key => {
        if (publishState[key] !== serverPublishState[key] && publishState[key]) {
          switch (key) {
            case 'audio':
              if (!this.localMainAudioTrack || !this.localMainAudioTrack.isMediaTrackActive) return false;
              return true;
            case 'bigVideo':
            case 'smallVideo':
              if (!this.localMainVideoTrack || !this.localMainVideoTrack.isMediaTrackActive) return false;
              return true;
            case 'auxVideo':
              if (!this.localAuxVideoTrack || !this.localAuxVideoTrack.isMediaTrackActive) return false;
              return true;
          }
        }
        return false;
      });

      if (notMatchKeyList.length > 0) {
        if (isSecondCheck) {
          kvStatManager.addCount({ key: KV_REPORT_KEY_NETWORK.PUBLISH_STATE_NOT_MATCHED });
          notMatchKeyList.forEach(key => {
            this._log.warn(`${key} publish failed during call ${getOSString()} ${getDeviceModel()}`);
            kvStatManager.addEnum({ key: KV_REPORT_KEY_NETWORK.PUBLISH_FAILED_DURING_CALL, value: PUBLISH_FAILED_DURING_CALL_KEY_VALUE[key] });
          });
        } else {
          this._checkPublishStateTimeoutId = Timer.run(TIMER_TYPE.TIMEOUT, () => this.checkPublishState(true), { delay: 10 * 1000, count: 1 });
          return;
        }
        Timer.clearTask(this._checkPublishStateTimeoutId);
        this._checkPublishStateTimeoutId = -1;
      }
    } catch (error) {
      this._log.warn('checkPublishState failed', error);
    }
  }

  get isMainStreamPublished() {
    return !!(this.localMainAudioTrack || this.localMainVideoTrack);
  }

  get isAuxStreamPublished() {
    return !!(this.localAuxVideoTrack || this.localAuxAudioTrack);
  }

  isTrackReferenced(localTrack: ILocalTrack): boolean {
    return localTrack === this.localMainAudioTrack || localTrack === this.localMainVideoTrack ||
      localTrack === this.localAuxVideoTrack || localTrack === this.localAuxAudioTrack;
  }

  get serverPublishState() {
    return {
      audio: !!(this.flag & AUDIO_STAT_BIT),
      bigVideo: !!(this.flag & VIDEO_STAT_BIT),
      smallVideo: !!(this.flag & SMALL_STAT_BIT),
      auxVideo: !!(this.flag & AUX_STAT_BIT)
    };
  }

  initialize() {
    this.installEvents();
  }

  close(reason?: string) {
    const senders = this._peerConnection?.getSenders() || [];
    for (const sender of senders) {
      sender.replaceTrack(null);
    }
    super.close(reason);
    this.uninstallEvents();
    this.uninstallTrackMuteEvents(this.localMainAudioTrack, this.localMainVideoTrack, this.localAuxVideoTrack);
    this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);
  }

  /**
   *
   * @private
   * @memberof Connection
   */
  installEvents() {
    if (!this.listeners(ConnectionEvent.CONNECTION_STATE_CHANGED).includes(this.handleConnectionStateChange)) {
      this.on(
        ConnectionEvent.CONNECTION_STATE_CHANGED,
        this.handleConnectionStateChange,
        this
      );
    }
    this.installSPCEvents();
  }

  installSPCEvents() {
    if (!this.singlePC?.listeners(SPC_EVENT.RECONNECTED).includes(this.onSinglePCReconnected)) {
      this.singlePC?.on(SPC_EVENT.RECONNECTED, this.onSinglePCReconnected, this);
    }
  }

  uninstallSPCEvents() {
    this.singlePC?.off(SPC_EVENT.RECONNECTED, this.onSinglePCReconnected, this);
  }

  uninstallEvents() {
    this.off(
      ConnectionEvent.CONNECTION_STATE_CHANGED,
      this.handleConnectionStateChange,
      this
    );
    this.uninstallSPCEvents();
  }

  emitConnectionStateChangedEvent(state: ConnectionState, videoTrack?: LocalVideoTrack | LocalScreenTrack | null) {
    const prevState = this._currentState;
    const result = super.emitConnectionStateChangedEvent(state);
    if (result && prevState !== state) {
      if (videoTrack) {
        videoTrack.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState, state });
      } else {
        this.localMainVideoTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState, state });
        this.localAuxVideoTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState, state });
        this._publishingLocalVideoTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState, state });
      }
    }
    return result;
  }

  async onVideoEncodeFailed(localVideoTrack: LocalVideoTrack) {
    if (!localVideoTrack || !localVideoTrack.isMediaTrackActive) return;

    const { videoCodec, singlePC } = this;
    if (!singlePC) return;

    const fallbackMap = {
      [VideoCodec.H265]: {
        supported: singlePC.isH264EncodeSupported,
        target: VideoCodec.H264,
        log: 'h265 encoder not working'
      },
      [VideoCodec.H264]: {
        supported: singlePC.isVP8EncodeSupported,
        target: VideoCodec.VP8,
        log: 'h264 encoder not working'
      },
      [VideoCodec.VP8]: {
        supported: false,
        target: VideoCodec.VP8,
        log: 'vp8 encoder not working, no fallback available'
      }
    };
    if (videoCodec === VideoCodec.VP9 || videoCodec === VideoCodec.AV1) {
      return;
    }
    const fallback = fallbackMap[videoCodec];
    this._log.warn(fallback.log);
    if (fallback?.supported) {
      await singlePC.switchVideoEncoder(fallback.target);
    }
  }

  async publish({ localAudioTrack, localVideoTrack, isAuxiliary }: { localAudioTrack?: LocalAudioTrack | null, localVideoTrack?: LocalVideoTrack | null, isAuxiliary: boolean; }) {
    if (!this.singlePC) return;
    this.installEvents();
    this.installTrackMuteEvents(localAudioTrack, localVideoTrack);
    if (localVideoTrack) {
      localVideoTrack.retryEncodeFailed = this.onVideoEncodeFailed.bind(this);
      if (IS_IOS && (isVersionLargerThan(IOS_VERSION, '26.2', true) || (isVersionLargerThan(SAFARI_VERSION, '26.2', true)) || (IS_WECHAT && isVersionLargerThan(IOS_VERSION, '18.7', true)))) {
        this._log.warn('detectH264Supported for fallback 26.2 video encode issue');
        try {
          await rtcDetector.detectH264SupportedByFakeStreaming(500);
        } catch (error) {}
      }
    }

    await this.singlePC.waitForPeerConnectionConnected();
    if (localAudioTrack) {
      this._publishingLocalAudioTrack = localAudioTrack;
    }
    if (localVideoTrack) {
      if (!this.singlePC.isH264EncodeSupported && !this.singlePC.isVP8EncodeSupported) {
        throw new RtcError({
          code: ErrorCode.NOT_SUPPORTED_H264,
          message: generateErrorMessage({ key: ErrorName.NOT_SUPPORTED_H264ENCODE })
        });
      }
      if (this.singlePC.isUsingH264 && !this.singlePC.isH264EncodeSupported && this.singlePC.isVP8EncodeSupported) {
        this._log.warn('h264 encoder not supported');
        await this.singlePC.switchVideoEncoder(VideoCodec.VP8);
      }
      // 规避 Android Chrome 115 版本 bug，fallback 分辨率到 480p
      // https://bugs.chromium.org/p/chromium/issues/detail?id=1469318
      if (IS_ANDROID && getChromeMajorVersion() === 115 && localVideoTrack.profile.width * localVideoTrack.profile.height <= 640 * 360) {
        this._log.warn(`fallback video to defaultBigVideoProfile: ${JSON.stringify(defaultBigVideoProfile)}`);
        localVideoTrack.setProfile(defaultBigVideoProfile);
        await localVideoTrack.applyProfile();
      }
      this._publishingLocalVideoTrack = localVideoTrack;
    }
    this._isPublishingAux = isAuxiliary;

    let smallTrack: MediaStreamTrack | undefined = undefined;
    // 初始化小流
    if (localVideoTrack && !isAuxiliary && localVideoTrack.small) {
      smallTrack = this._room.videoManager.smallTrack;
    }

    await this._signalChannel.sendWaitForResponseWithRetry({
      command: SignalCommand.SPC_PUBLISH,
      responseCommand: SignalEvent.SPC_PUBLISH_RESULT,
      data: { ...this.singlePC.uplinkSSRC, state: this._room.publishState, muteState: this._room.muteState },
      retries: 3
    });

    if (localVideoTrack) {
      await this.checkHighProfile({ streamType: localVideoTrack.streamType, newWidth: localVideoTrack.settings.width, newHeight: localVideoTrack.settings.height });
    }

    await this.publishByTransceiver({ localAudioTrack, localVideoTrack, smallTrack, isAuxiliary });

    this._publishingLocalAudioTrack = null;
    this._publishingLocalVideoTrack = null;
    this._isPublishingAux = false;

    if (localVideoTrack) {
      this[isAuxiliary ? 'localAuxVideoTrack' : 'localMainVideoTrack'] = localVideoTrack;
      await this.singlePC.setDegradationPreference(this._peerConnection!.getSenders()[isAuxiliary ? 3 : 1]!, localVideoTrack.contentHint, localVideoTrack.streamType);
      const { scaleResolutionDownBy } = localVideoTrack;
      await this.singlePC.setScaleResolutionDownBy(this._peerConnection!.getSenders()[isAuxiliary ? 3 : 1]!, scaleResolutionDownBy, localVideoTrack.streamType);
    }
    if (localAudioTrack) {
      this[isAuxiliary || localAudioTrack instanceof LocalScreenAudioTrack ? 'localAuxAudioTrack' : 'localMainAudioTrack'] = localAudioTrack;
    }
    await this.singlePC.setBandwidth({
      audio: this.localMainAudioTrack?.profile.bitrate || this.localAuxAudioTrack?.profile.bitrate,
      bigVideo: this.localMainVideoTrack?.profile.bitrate,
      smallVideo: this.localMainVideoTrack?.small?.bitrate,
      auxVideo: this.localAuxVideoTrack?.profile.bitrate
    });
    this.sendMediaSettings();
    const streamType = isAuxiliary ? MULTI_VIDEO_DATA_TYPE.MULTI_DATA_AUX_IMG : MULTI_VIDEO_DATA_TYPE.MULTI_DATA_BIG_IMG;
    if ((this._room.preferHW || this._room.scheduleResult.config?.preferHW) && localVideoTrack && localVideoTrack.profile.width * localVideoTrack.profile.height >= 1280 * 720) {
      this.singlePC.useHWEncoder(true, streamType);
    }
  }

  // Chrome 69+, Safari 11+, Firefox 59+
  publishByTransceiver({ localAudioTrack, localVideoTrack, smallTrack, isAuxiliary }: { localAudioTrack?: LocalAudioTrack | null, localVideoTrack?: LocalVideoTrack | null, smallTrack?: MediaStreamTrack, isAuxiliary: boolean; }) {
    if (!isAddTransceiverSupported()) return;
    this._log.info('publish by transceiver');

    const videoTrack = localVideoTrack?.outMediaTrack;
    const audioTrack = localAudioTrack?.outMediaTrack;

    const transceivers = this._peerConnection!.getTransceivers();
    const promiseList = [];
    const transceiverIndexList: number[] = [];
    const addTrackToTransceiver = (mediaType: MediaType, i: number, track: MediaStreamTrack) => {
      const promise = transceivers[i].sender.replaceTrack(track);
      transceiverIndexList.push(i);
      if (this.singlePC?.enableInsertableStreams) {
        promise.then(() => this.createEncodedStreams(transceivers[i].sender, mediaType));
      }
      this.initSenderTransform(transceivers[i].sender, mediaType);
      promiseList.push(promise);
    };
    if (audioTrack) addTrackToTransceiver(localAudioTrack.mediaType, 0, audioTrack);
    if (videoTrack) addTrackToTransceiver(localVideoTrack.mediaType, isAuxiliary ? 3 : 1, videoTrack);
    if (localVideoTrack?.small) promiseList.push(this.publishSmall(this._room.videoManager.smallMode, localVideoTrack));
    const p = this.singlePC!.setTransceiverDirection(TRANSPORT_DIRECTION.SENDONLY, transceiverIndexList);
    promiseList.push(p);

    return Promise.all(promiseList);
  }
  getTrackByMediaType(mediaType: MediaType) {
    switch (mediaType) {
      case MediaType.AUDIO:
        return this.localMainAudioTrack || this._room.localMainAudioTrack;
      case MediaType.BIG_VIDEO:
      case MediaType.SMALL_VIDEO:
        return this.localMainVideoTrack || this._room.localMainVideoTrack;
      case MediaType.AUX_VIDEO:
        return this.localAuxVideoTrack || this._room.localAuxVideoTrack;
      default:
        return null;
    }
  }
  createEncodedStreams(sender: RTCRtpSender, mediaType: MediaType) {
    if (this.singlePC!.insertableStreamsAbortMap.has(sender)) {
      return;
    }
    // @ts-ignore
    const streams = sender.createEncodedStreams() as { readable: ReadableStream<RTCEncodedVideoFrame>; writable: WritableStream<RTCEncodedVideoFrame>; };
    const abortController = new AbortController();
    this.singlePC?.addAbortController(sender, abortController);
    const readable = this.getTrackByMediaType(mediaType)?.enableEncodeFrame ? streams.readable.pipeThrough(new TransformStream({
      transform: (chunk, controller) => {
        const localTrack = this.getTrackByMediaType(mediaType);
        if (!localTrack || !localTrack.encodeFrame) return controller.enqueue(chunk);
        if (localTrack.isAudio) {
          controller.enqueue(localTrack.enableEncodeFrame ? localTrack.encodeFrame(chunk) : chunk);
        } else {
          controller.enqueue((this.singlePC?.isUsingH264 || this.singlePC?.isUsingH265) ? localTrack.encodeFrame(chunk, mediaType === MediaType.SMALL_VIDEO) : chunk);
        }
      }
    }), abortController) : streams.readable;
    readable.pipeTo(streams.writable, abortController).catch((error: any) => {
      this._log.debug('encoded stream error', error);
      if (error !== 'destroy') this._log.warn(error);
    });
  }

  initSenderTransform(sender: RTCRtpSender, mediaType: MediaType) {
    if (!this._peerConnection || !this.singlePC || !this.singlePC.scriptTransformWorker || !IS_SCRIPT_TRANSFORM_SUPPORTED) return;
    const isMain = mediaType !== MediaType.AUX_VIDEO;
    const isSmall = mediaType === MediaType.SMALL_VIDEO;
    if (!sender.transform) {
      sender.transform = new RTCRtpScriptTransform(this.singlePC.scriptTransformWorker, { isReceiver: false, isAudio: mediaType === MediaType.AUDIO, isMain, isSmall });
    }
  }

  async enableSmall(needEnable: boolean) {
    if (needEnable) {
      await this.publishSmall(this._room.videoManager.smallMode);
    } else {
      await this.unpublishSmall();
    }
  }

  async publishSmall(smallMode: SMALL_MODE, bigVideoTrack = this.localMainVideoTrack) {
    if (!this.singlePC) return;
    if (smallMode === SMALL_MODE.CANVAS && !isCanvasSmallStreamSupported()) {
      this._log.warn('canvas mode small stream is not supported');
      return;
    }
    const transceivers = this._peerConnection!.getTransceivers();
    const { sender } = transceivers[2];

    const smallModeResult = await this.doPublishSmall(smallMode, bigVideoTrack);

    const key = smallMode === SMALL_MODE.CANVAS ? KV_REPORT_KEY_SMALL_MODE.SMALL_MODE_CANVAS : KV_REPORT_KEY_SMALL_MODE.SMALL_MODE_API;
    kvStatManager.addSuccessEvent({ key });

    if (!smallModeResult) {
      kvStatManager.addFailedEvent({ key, error: SMALL_MODE_ERROR_CODE.INIT_FAILED });
      return;
    }
    // sei, dump
    if (this.singlePC?.enableInsertableStreams) this.createEncodedStreams(sender, MediaType.SMALL_VIDEO);
    this.initSenderTransform(sender, MediaType.SMALL_VIDEO);

    await this.singlePC.setTransceiverDirection(TRANSPORT_DIRECTION.SENDONLY, [2]);
    this.updateMediaSettings();
    await this.doPublishChange();

    if (sender.track) {
      this._blackSmallVideoDetectionId = blackVideoDetector.start({
        track: sender.track as MediaStreamVideoTrack,
        room: this._room,
        isUplink: true,
        userId: this.userId,
        onBlack: () => {
          this._log.warn('small video is black');
          const key = smallMode === SMALL_MODE.CANVAS ? KV_REPORT_KEY_SMALL_MODE.SMALL_MODE_CANVAS : KV_REPORT_KEY_SMALL_MODE.SMALL_MODE_API;
          kvStatManager.addFailedEvent({ key, error: SMALL_MODE_ERROR_CODE.BLACK_SCREEN_FAILED });
          blackVideoDetector.stop(this._blackSmallVideoDetectionId);
          this._blackSmallVideoDetectionId = undefined;
        }
      });
    }
  }
  async doPublishSmall(smallMode: SMALL_MODE, bigVideoTrack: LocalVideoTrack | LocalScreenTrack | null | undefined = this.localMainVideoTrack): Promise<SMALL_MODE | null> {
    if (!this.singlePC) return null;
    this._log.info('publish small', smallMode);
    const transceivers = this._peerConnection!.getTransceivers();
    const { sender } = transceivers[2];

    if (smallMode === SMALL_MODE.CANVAS && this._room.videoManager.smallTrack) {
      await sender.replaceTrack(this._room.videoManager.smallTrack);
      return SMALL_MODE.CANVAS;
    }
    if (smallMode === SMALL_MODE.API && bigVideoTrack?.outMediaTrack && bigVideoTrack?.small) {
      await sender.replaceTrack(bigVideoTrack?.outMediaTrack);
      const parameters = sender.getParameters();
      const scaleResolutionDownBy = calculateScaleResolutionDownNumber(bigVideoTrack?.profile, bigVideoTrack?.small);
      this._log.info('small scaleResolutionDownBy', scaleResolutionDownBy);
      parameters.encodings[0].scaleResolutionDownBy = scaleResolutionDownBy;
      sender.setParameters(parameters);
      return SMALL_MODE.API;
    }

    this._log.warn(`small track can not be enabled, smallMode: ${this._room.videoManager.smallMode}, smallTrack: ${!!this._room.videoManager.smallTrack}, bigVideoTrack: ${!!bigVideoTrack?.outMediaTrack}`);
    return null;
  }

  async unpublishSmall() {
    if (!this.singlePC) return;
    this._log.info('unpublish small');
    const transceivers = this._peerConnection!.getTransceivers();
    await transceivers[2].sender.replaceTrack(null);
    await this.singlePC.setTransceiverDirection(TRANSPORT_DIRECTION.INACTIVE, [2]);
    this.updateMediaSettings();
    await this.doPublishChange();
    blackVideoDetector.stop(this._blackSmallVideoDetectionId);
    this._blackSmallVideoDetectionId = undefined;
  }

  /**
   * 检查并调整 H264 profile-level-id
   * 根据分辨率和设备能力决定使用 high 还是 non-high
   */
  async checkHighProfile(event: { streamType: RemoteStreamType, newWidth: number, newHeight: number }) {
    const scheduleProfileLevelId = this._room.scheduleResult.config?.profileLevelId || {};
    const type = event.streamType === RemoteStreamType.Main ? 'big' : 'aux';
    // 此设备未被下发 high，不处理
    if (scheduleProfileLevelId[type] !== 'high') return;

    const pixels = event.newWidth * event.newHeight;
    const shouldUseHigh = pixels >= 1280 * 720 && !isAMDGPU();

    try {
      await this.singlePC?.setH264ProfileLevelId(event.streamType, shouldUseHigh);
    } catch (err) {
      this._log.warn('setH264ProfileLevelId failed, ignore', err);
    }
  }

  installTrackMuteEvents(...localTracks: (ILocalTrack | undefined | null)[]) {
    localTracks.forEach(localTrack => {
      if (localTrack) {
        localTrack?.on(TrackEvent.MUTE, this.sendMutedFlag, this);
        localTrack?.on(TrackEvent.UNMUTE, this.sendMutedFlag, this);
      }
    });
  }

  uninstallTrackMuteEvents(...localTracks: (ILocalTrack | undefined | null)[]) {
    localTracks.forEach(localTrack => {
      if (localTrack) {
        localTrack?.off(TrackEvent.MUTE, this.sendMutedFlag, this);
        localTrack?.off(TrackEvent.UNMUTE, this.sendMutedFlag, this);
      }
    });
  }


  @after(({ localVideoTrack }: { localAudioTrack?: LocalAudioTrack | null, localVideoTrack?: LocalVideoTrack | null, isAuxiliary: boolean; }) => {
    delete localVideoTrack?.retryEncodeFailed;
  })
  async unpublish({ localAudioTrack, localVideoTrack }: { localAudioTrack?: LocalAudioTrack | null, localVideoTrack?: LocalVideoTrack | null; }) {
    await this.singlePC?.waitForPeerConnectionConnected();
    const isAuxiliary = (localVideoTrack && localVideoTrack === this.localAuxVideoTrack) || (localAudioTrack && localAudioTrack === this.localAuxAudioTrack);
    const videoTrack = localVideoTrack?.outMediaTrack;
    const senders = this._peerConnection!.getSenders();
    const transceiverIndexList = [];

    if (localAudioTrack) {
      if (isAuxiliary) {
        this.localAuxAudioTrack = null;
      } else {
        this.localMainAudioTrack = null;
      }
      if (!this.localMainAudioTrack && !this.localAuxAudioTrack) {
        await senders[0].replaceTrack(null);
        transceiverIndexList.push(0);
      }
    }

    if (videoTrack) {
      if (isAuxiliary) {
        await senders[3].replaceTrack(null);
        this.localAuxVideoTrack = null;
        this._mediaSettings = {
          ...this._mediaSettings,
          auxVideoBps: 0,
          auxVideoFps: 0,
          auxVideoWidth: 0,
          auxVideoHeight: 0
        };
        this.singlePC?.clearStartBitrate(3);
        transceiverIndexList.push(3);
      } else {
        await senders[1].replaceTrack(null);
        await senders[2].replaceTrack(null);
        this.localMainVideoTrack = null;
        this._mediaSettings = {
          ...this._mediaSettings,
          videoWidth: 0,
          videoHeight: 0,
          videoBps: 0, // bps
          videoFps: 0,
          audioFs: 0, // 采样率
          audioChannel: 0, // 声道数
          audioBps: 0, // 单位bps

          smallVideoWidth: 0,
          smallVideoHeight: 0,
          smallVideoFps: 0,
          smallVideoBps: 0
        };
        this.singlePC?.clearStartBitrate(1);
        transceiverIndexList.push(1, 2);
      }
    }
    if (this.isMainStreamPublished || this.isAuxStreamPublished) {
      await this.singlePC!.setTransceiverDirection(TRANSPORT_DIRECTION.INACTIVE, transceiverIndexList);
      await this.doPublishChange(false);
    } else {
      await this.doUnpublish();
    }
    this.uninstallTrackMuteEvents(localAudioTrack, localVideoTrack);
    localVideoTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState: this._currentState, state: ConnectionState.DISCONNECTED });
  }

  @skipIf<UplinkConnectionSPC>({
    when() {
      return this.isDestroyed;
    }
  })
  async doPublishChange(enableLogWhenTimeout = true) {
    const data = {
      state: this._room.publishState,
      constraintConfig: this._mediaSettings
    };
    const result = await this._signalChannel.sendWaitForResponseWithRetry({
      command: SignalCommand.PUBLISH_STATE_CHANGE,
      data,
      responseCommand: SignalEvent.PUBLISH_STATE_CHANGE_RESULT,
      enableLog: enableLogWhenTimeout,
      retries: 3
    });
    this.checkPublishResultCode(result.data.code, result.data.message);
  }

  @skipIf<UplinkConnectionSPC>({
    when() {
      return this.isDestroyed;
    }
  })
  doUnpublish(enableLogWhenTimeout = false): Promise<void | { data: SignalResponse; }> {
    return this._signalChannel
      .sendWaitForResponse({
        command: SignalCommand.UNPUBLISH,
        commandDesc: 'unpublish',
        responseCommand: SignalEvent.UNPUBLISH_RESULT,
        enableLog: enableLogWhenTimeout
      }).catch((error: RtcError) => {
        if (error.getCode() === ErrorCode.API_CALL_TIMEOUT || error.getCode() === ErrorCode.API_CALL_ABORTED) {
          return Promise.resolve();
        }
        throw error;
      });
  }

  updateMediaSettings() {
    this._mediaSettings.videoCodec = this.singlePC?.videoCodec || 'h264';
    // 通知后台当前解码类型。如果协商支持H265 实际解码失败，需要改成H264
    this._mediaSettings.videoDecCodec = this.singlePC?.downlinkVideoCodec || 'h264';
    const localAudioTrack = this._publishingLocalAudioTrack || this.localMainAudioTrack || this.localAuxAudioTrack;
    let { localMainVideoTrack, localAuxVideoTrack } = this;
    if (this._publishingLocalVideoTrack) {
      if (this._isPublishingAux) {
        localAuxVideoTrack = this._publishingLocalVideoTrack;
      } else {
        localMainVideoTrack = this._publishingLocalVideoTrack;
      }
    }
    if (IS_GET_SETTINGS_SUPPORTED) {
      if (localAudioTrack && localAudioTrack.outMediaTrack) {
        const settings = localAudioTrack.outMediaTrack.getSettings();
        // @ts-ignore
        this._mediaSettings.audioChannel = settings.channelCount || 1;
        this._mediaSettings.audioBps = localAudioTrack.profile.bitrate * 1000; // bps
        this._mediaSettings.audioFs = settings.sampleRate || 0;
      }

      if (localMainVideoTrack && localMainVideoTrack.outMediaTrack) {
        const settings = localMainVideoTrack.outMediaTrack.getSettings();
        const { scaleResolutionDownBy } = localMainVideoTrack;
        this._mediaSettings.videoWidth = (settings.width || 0) / scaleResolutionDownBy || 0;
        this._mediaSettings.videoHeight = (settings.height || 0) / scaleResolutionDownBy || 0;
        this._mediaSettings.videoFps = settings.frameRate || 0;
        this._mediaSettings.videoBps = localMainVideoTrack.profile.bitrate * 1000; // bps

        if (localMainVideoTrack.small) {
          this._mediaSettings.smallVideoWidth = localMainVideoTrack.small.width;
          this._mediaSettings.smallVideoHeight = localMainVideoTrack.small.height;
          this._mediaSettings.smallVideoFps = localMainVideoTrack.small.frameRate;
          this._mediaSettings.smallVideoBps = localMainVideoTrack.small.bitrate * 1000;
        }
      }
      if (localAuxVideoTrack && localAuxVideoTrack.outMediaTrack) {
        const settings = localAuxVideoTrack.outMediaTrack.getSettings();
        const { scaleResolutionDownBy } = localAuxVideoTrack;
        this._mediaSettings.auxVideoWidth = (settings.width || 0) / scaleResolutionDownBy || 0;
        this._mediaSettings.auxVideoHeight = (settings.height || 0) / scaleResolutionDownBy || 0;
        this._mediaSettings.auxVideoFps = settings.frameRate || 0;
        this._mediaSettings.auxVideoBps = localAuxVideoTrack.profile.bitrate * 1000; // bps
      }
    } else {
      if (localAudioTrack && localAudioTrack.outMediaTrack) {
        this._mediaSettings.audioChannel = localAudioTrack.profile.channelCount;
        this._mediaSettings.audioBps = localAudioTrack.profile.bitrate * 1000; // bps
        this._mediaSettings.audioFs = localAudioTrack.profile.sampleRate;
      }
      if (localMainVideoTrack && localMainVideoTrack.outMediaTrack) {
        this._mediaSettings.videoWidth = localMainVideoTrack.profile.width;
        this._mediaSettings.videoHeight = localMainVideoTrack.profile.height;
        this._mediaSettings.videoFps = localMainVideoTrack.profile.frameRate;
        this._mediaSettings.videoBps = localMainVideoTrack.profile.bitrate * 1000; // bps
      }
    }

    this._log.info(`updateMediaSettings: ${JSON.stringify(this._mediaSettings)}`);
  }

  // 调用时机：本地推流后，业务侧调用 localStream.setVideoProfile 更新码率时。
  sendMediaSettings() {
    this.updateMediaSettings();
    this._signalChannel
      .sendWaitForResponse({
        command: SignalCommand.UPDATE_CONSTRAINT_CONFIG,
        data: this._mediaSettings,
        responseCommand: SignalEvent.UPDATE_CONSTRAINT_CONFIG_RES
      })
      .then(res => {
        if (res.data.code !== 0) {
          this._log.warn(res.data.message);
        }
      })
      .catch(() => { });
  }

  /**
   * 向 peerConnection 添加 track
   *
   * Chrome 69+ 使用 transceiver 方案
   * Chrome 56 ~ 68 使用旧版 peerConnection.addTrack 方案，向下兼容。
   * @param {MediaStreamTrack} track
   * @param {LocalStream} stream
   */
  async addTrack(localTrack: ILocalTrack) {
    if (!this._peerConnection) {
      return;
    }

    const isAuxiliary = localTrack === this.localAuxAudioTrack || localTrack === this.localAuxVideoTrack;

    this._log.info(`is adding ${localTrack.kind} track to current published local ${isAuxiliary ? NAME.AUXILIARY : NAME.MAIN} stream`);

    if (isGetTransceiversSupported()) {
      await this.addTrackByTransceiver(localTrack, isAuxiliary);
    }
  }

  // Chrome 69+
  async addTrackByTransceiver(localTrack: ILocalTrack, isAuxiliary: boolean) {
    if (!localTrack.mediaTrack) return;
    const transceivers = this._peerConnection!.getTransceivers();
    if (localTrack.kind === NAME.AUDIO) {
      await transceivers[0].sender.replaceTrack(localTrack.outMediaTrack);
    } else {
      const transceiverIndex = isAuxiliary ? 3 : 1;
      await transceivers[transceiverIndex].sender.replaceTrack(localTrack.outMediaTrack);

      if (transceiverIndex === 1 && this.localMainVideoTrack?.small && this._room.videoManager.smallTrack) {
        await transceivers[2].sender.replaceTrack(this._room.videoManager.smallTrack);
      }
      if (transceivers[transceiverIndex].direction === TRANSPORT_DIRECTION.INACTIVE) {
        await this.singlePC!.setTransceiverDirection(TRANSPORT_DIRECTION.SENDONLY, [transceiverIndex]);
      }
    }
    this.updateMediaSettings();
    await this.doPublishChange();
  }

  /**
   * 向 peerConnection 移除 track
   *
   * Chrome 69+ 使用 transceiver 方案
   * Chrome 56 ~ 68 使用旧版 peerConnection.addTrack 方案，向下兼容。
   */
  async removeTrack(localTrack: ILocalTrack) {
    if (!this._peerConnection) {
      return;
    }

    const isAuxiliary = localTrack === this.localAuxAudioTrack || localTrack === this.localAuxVideoTrack;


    this._log.info(`is removing ${localTrack.kind} track from current published local ${isAuxiliary ? NAME.AUXILIARY : NAME.MAIN} stream`);

    if (isGetTransceiversSupported()) {
      await this.removeTrackByTransceiver(localTrack, isAuxiliary);
    }
  }

  async removeTrackByTransceiver(localTrack: ILocalTrack, isAuxiliary: boolean) {
    if (!localTrack.mediaTrack) return;
    const transceivers = this._peerConnection!.getTransceivers();
    if (localTrack.kind === NAME.AUDIO) {
      await transceivers[0].sender.replaceTrack(null);
    } else {
      const transceiverIndex = isAuxiliary ? 3 : 1;
      await transceivers[transceiverIndex].sender.replaceTrack(null);
      if (transceiverIndex === 1 && this._room.videoManager.hasSmall) {
        await transceivers[2].sender.replaceTrack(null);
      }
      await this.singlePC!.setTransceiverDirection(TRANSPORT_DIRECTION.INACTIVE, [transceiverIndex]);
    }

    this.updateMediaSettings();
    await this.doPublishChange();
  }

  async replaceTrack(localTrack: ILocalTrack) {
    const senders = this._peerConnection?.getSenders();
    const track = localTrack.outMediaTrack || localTrack.mediaTrack;
    if (!senders || senders.length === 0 || !track || senders.find(sender => sender.track === track)) {
      return false;
    }
    const isAuxiliary = localTrack.mediaType === MediaType.AUX_VIDEO || localTrack === this.localAuxAudioTrack || localTrack === this.localAuxVideoTrack;
    this._log.info(`is replacing ${track.kind} track ${track.id} ${track.label} on ${isAuxiliary ? NAME.AUXILIARY : NAME.MAIN} stream`);
    if (track.kind === NAME.AUDIO && senders[0]) {
      await senders[0].replaceTrack(track);
    }

    if (track.kind === NAME.VIDEO) {
      if (!isAuxiliary && senders[1]) {
        await senders[1].replaceTrack(track);
      }
      if (isAuxiliary && senders[3]) {
        await senders[3].replaceTrack(track);
      }
    }
    return true;
  }

  /**
   * 设置上行音视频码率，策略如下：
   * 1. 优先使用 set maxBitrate 方式控制码率；
   * 优点：各浏览器均支持，不需要修改 SDP
   * 缺点：需要考虑低版本兼容性
   * 2. 若 set maxBitrate 不支持或者失败了，则使用
   * 修改 SDP 方式控制码率，作为备选方案。
   *
   * ref: https://developer.mozilla.org/en-US/docs/Web/API/RTCRtpEncodingParameters/maxBitrate
   * @private
   * @param {Number} bandwidth 码率，单位：kbps
   * @param {String} type video audio
   * @param {String} videoType big small auxiliary
   * @param {String} sdp
   * @return {String} sdp
   * @memberof Connection
   */
  async setBandwidth({ bandwidth, type, videoType }: BandWidthOption) {
    if (this.singlePC) {
      const config: Parameters<SinglePeerConnection['setBandwidth']>[0] = {};
      if (type === NAME.AUDIO) {
        config.audio = bandwidth;
      } else if (videoType === 'big') {
        config.bigVideo = bandwidth;
      } else if (videoType === 'small') {
        config.smallVideo = bandwidth;
      } else {
        config.auxVideo = bandwidth;
      }
      await this.singlePC.setBandwidth(config);
    }
  }

  /**
   * 发送 muteState，更新当前上行的 mute 状态
   *
   * 调用时机：
   * 1. 接入侧调用 muteAudio/muteVideo/unmuteAudio/unmuteVideo 时
   * 2. 推流成功后需要同步本地的 muteFlag，
   *    2.1. 修复推流前 muteAudio & muteVideo 再推流，mute 状态不准的问题
   *    ref: http://tapd.oa.com/TerWebG/bugtrace/bugs/view/1020396022084583223
   *    2.2. 修复重新推流后，mute 状态不准的问题
   *    ref: http://tapd.oa.com/20396022/bugtrace/bugs/view?bug_id=1020396022084979351
   */
  sendMutedFlag(localTrack?: ILocalTrack) {
    const isAuxiliary = localTrack === this.localAuxAudioTrack || localTrack === this.localAuxVideoTrack;
    if (isAuxiliary) return;

    this._log.info(`send muted state: ${JSON.stringify(this._room.muteState)}`);
    this._signalChannel.sendWaitForResponseWithRetry({
      command: SignalCommand.UPDATE_MUTE_STAT,
      responseCommand: SignalEvent.MUTE_RESULT,
      data: this._room.muteState,
      retries: 3
    }).catch(() => { });
  }

  handleConnectionStateChange(event: { prevState: ConnectionState, state: ConnectionState; }) {
    if (event.state === ConnectionState.CONNECTED) {
      if (this.localMainVideoTrack || (this._publishingLocalVideoTrack && !this._isPublishingAux)) {
        innerEmitter.emit(INNER_EVENT.SEND_FIRST_VIDEO_FRAME, { room: this._room });
      }
    }
  }

  getVideoTrackId(type: string = NAME.VIDEO) {
    // 优先从 sender 中获取 trackId
    // iOS 15.1 通过推 canvas track 来规避 crash 问题，
    // localStream.getVideoTrack() 并不是真实推出去的 canvas track
    if (this._peerConnection) {
      const senders = this._peerConnection.getSenders();

      if (type === NAME.AUXILIARY && senders[3] && senders[3].track) {
        return senders[3].track.id;
      }
      if (type === NAME.VIDEO && senders[1] && senders[1].track) {
        return senders[1].track.id;
      }
    }
    if (this.localMainVideoTrack && type === NAME.VIDEO) {
      const videoTrack = this.localMainVideoTrack.mediaTrack;
      if (videoTrack) {
        return videoTrack.id;
      }
    }
    if (this.localAuxVideoTrack && type === NAME.AUXILIARY) {
      const videoTrack = this.localAuxVideoTrack.mediaTrack;
      if (videoTrack) {
        return videoTrack.id;
      }
    }
    return '';
  }

  getSSRC() {
    return this.ssrc;
  }

  /**
   * publish、publish_change 信令响应 code 处理
   * @param {Number} code 信令响应 code
   * @param {String} message 信令响应 message
   * @returns
   */
  checkPublishResultCode(code: number, message: string) {
    if (code === 0) {
      return;
    }
    if (code === NOT_SUPPORTED_H264) {
      // 支持VP8上行但 sdkappid 不在白名单，后端会返回这个 code 状态码
      this._log.error(ErrorInfo.NOT_SUPPORTED_H264ENCODE);
      // 在client 需要捕获该异常，避免重复调用 Publish
      throw new RtcError({
        code: ErrorCode.NOT_SUPPORTED_H264,
        message: generateErrorMessage({ key: ErrorName.NOT_SUPPORTED_H264ENCODE })
      });
    } else {
      throw new RtcError({
        code: ErrorCode.UNKNOWN,
        message: generateErrorMessage({
          key: ErrorName.SIGNAL_RESPONSE_FAILED,
          data: { signalResponse: SignalEvent.PUBLISH_RESULT, code, message }
        })
      });
    }
  }

  async onSinglePCReconnected() {
    if (this.isMainStreamPublished) {
      this._log.warn('republish main stream');
      await this.publish({ localAudioTrack: this.localMainAudioTrack, localVideoTrack: this.localMainVideoTrack, isAuxiliary: false });
    }
    if (this.isAuxStreamPublished) {
      this._log.warn('republish aux stream');
      await this.publish({ localAudioTrack: this.localAuxAudioTrack, localVideoTrack: this.localAuxVideoTrack, isAuxiliary: true });
    }
  }
}

enum PUBLISH_FAILED_DURING_CALL_KEY_VALUE {
  audio = 1,
  bigVideo,
  smallVideo,
  auxVideo
}

export default UplinkConnectionSPC;
